import React, { useEffect, useRef, useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import UserInfo from "../../components/userinfo"
import Skeleton from "../../components/Skeleton"
import { useUser } from '../../contexts/UserContext'
import { API_CONFIG } from '../../config/apiConfig'
import './index.css'

export default function Home() {
    const navigate = useNavigate()
    const location = useLocation()
    const { userInfo, isAdmin, isDriver, isDirectDispatch, loading, refreshUserInfo, isBound, isNewFeatureExperience, checkNewFeatureExperienceStatus } = useUser()
    const [hasRefreshed, setHasRefreshed] = useState(false)
    const [isDeviceInstalled, setIsDeviceInstalled] = useState(false)
    const [unboundToast, setUnboundToast] = useState(null)
    // 【H6】用户信息自动重取次数上限：避免无 uid 降级状态下 loading→effect 无限循环刷新
    const userInfoRetryRef = useRef(0)

    const showUnboundToast = () => {
        setUnboundToast('应用未绑定无法使用该功能，请先绑定账号')
        setTimeout(() => setUnboundToast(null), 3000)
    }

    // 获取当前租户的设备安装状态
    useEffect(() => {
        setIsDeviceInstalled(API_CONFIG.isDeviceInstalled === 1);
    }, [loading, userInfo]);

    // 检查用户信息是否完整
    const isUserInfoComplete = () => {
        if (!userInfo) return false
        const hasUserId = userInfo.open_id || userInfo.user_id
        // 对于首页，只需要检查user_id是否存在
        // 权限状态(isAdmin, isDriver)在UserContext中单独检查
        return hasUserId !== ''
    }

    // 监听路由变化，当进入首页时自动刷新
    useEffect(() => {
        // 只有当路由是首页且还未刷新过时
        if (location.pathname === '/' && !hasRefreshed) {
            console.log('进入首页，自动刷新用户信息...')
            setHasRefreshed(true)
            
            // 延迟刷新，确保页面已完全加载
            const timer = setTimeout(() => {
                refreshUserInfo()
            }, 500)
            
            return () => clearTimeout(timer)
        }
    }, [location.pathname, hasRefreshed, refreshUserInfo])

    // 在首页刷新时，如果用户信息有缺失则重新获取（最多重试 2 次，防死循环）
    useEffect(() => {
        // 只有在加载完成后、用户信息不完整且还未刷新过时才重新获取
        if (!loading && !isUserInfoComplete() && hasRefreshed && userInfoRetryRef.current < 2) {
            console.log('用户信息不完整，重新获取...')
            userInfoRetryRef.current += 1
            refreshUserInfo()
        }
    }, [loading, userInfo, refreshUserInfo, hasRefreshed])

    // ⚠️ 2026-08-04 临时：去掉绑定校验，所有功能按钮直接放行（先让用户正常使用）
    const requireBound = (navigateFn) => {
        navigateFn()
    }

    const handleReserveCar = () => requireBound(() => navigate('/vehicle-select'))
    const handleMyReservations = () => requireBound(() => navigate('/my-reservations'))
    const handleApproval = () => requireBound(() => navigate('/car-approval'))
    const handleReturnApproval = () => requireBound(() => navigate('/car-return-approval'))
    const handleCarReport = () => requireBound(() => navigate('/car-report'))
    const handleDelayApproval = () => requireBound(() => navigate('/delay-approval'))
    const handleDispatchCar = () => requireBound(() => navigate('/dispatch'))
    const handleAdminConsole = () => requireBound(() => navigate('/admin-redirect'))
    // 私车三大模块（仅新功能体验名单用户可见）
    const handlePrivateCarApply = () => requireBound(() => navigate('/private-car-apply'))
    const handlePrivateCarRecord = () => requireBound(() => navigate('/private-car-record'))
    const handlePrivateCarReport = () => requireBound(() => navigate('/private-car-report'))

    // 体验名单校验：用户信息就绪后检查是否为新功能体验用户（控制私车模块入口可见性）
    useEffect(() => {
        const uid = userInfo?.open_id || userInfo?.user_id
        if (uid) {
            checkNewFeatureExperienceStatus(uid)
        }
    }, [userInfo, checkNewFeatureExperienceStatus])

    const handleCarControl = () => {
        navigate('/car-control')
    }

    const handleFeedback = () => {
        navigate('/feedback')
    }

    const handleSettings = () => {
        navigate('/bind-account')
    }

    const handleBindAccount = () => {
        navigate('/bind-account')
    }

    return (
        <div className="home">
            <UserInfo userInfo={userInfo} />
            {/* H2: 加载中显示骨架屏 */}
            {loading ? (
                <Skeleton type="card" count={4} />
            ) : (
                <div className="reserve-container">
                    {/* 预约用车 */}
                    <div className="reserve-card reserve-car-card" onClick={handleReserveCar}>
                        <h3>预约用车</h3>
                    </div>

                    {/* 预约记录 */}
                    <div className="reserve-card my-reservations-card" onClick={handleMyReservations}>
                        <h3>预约记录</h3>
                    </div>

                    {/* 我要控车 — 仅在已安装设备时显示 */}
                    {isDeviceInstalled && (
                        <div className="reserve-card car-control-card" onClick={handleCarControl}>
                            <h3>我要控车</h3>
                        </div>
                    )}
                    <div className="reserve-card private-car-apply-card" onClick={handlePrivateCarApply}>
                        <h3>私车公干</h3>
                    </div>
                    <div className="reserve-card private-car-record-card" onClick={handlePrivateCarRecord}>
                        <h3>私车记录</h3>
                    </div>
                    {/* 仅新功能体验名单用户可见（灰度测试） */}
               {/*     {isNewFeatureExperience && (
                        <>
                            <div className="reserve-card private-car-report-card" onClick={handlePrivateCarReport}>
                                <h3>私车报表</h3>
                            </div>
                        </>
                    )}*/}
                    {/* 意见反馈 */}
                    <div className="reserve-card feedback-card" onClick={handleFeedback}>
                        <h3>意见反馈</h3>
                    </div>

                    {/* 设置 */}
                    <div className="reserve-card settings-card" onClick={handleSettings}>
                        <h3>设置</h3>
                    </div>

                    {/* 私车报表 — 配置了私车报表权限的用户可见 */}
                    {isDirectDispatch && (
                         <div className="reserve-card private-car-report-card" onClick={handlePrivateCarReport}>
                                <h3>私车报表</h3>
                            </div>
                    )}

                    {/* 管理员专属卡片 */}
                    {isAdmin && (
                        <>
                            <div className="reserve-card dispatch-card" onClick={handleDispatchCar}>
                                <h3>直接派车</h3>
                            </div>
                            {/* 管理后台 */}
                            <div className="reserve-card admin-console-card" onClick={handleAdminConsole}>
                                <h3>管理后台</h3>
                            </div>

                            {/* 用车审批 */}
                            <div className="reserve-card car-approval-card" onClick={handleApproval}>
                                <h3>用车审批</h3>
                            </div>

                            {/* 延期审批 */}
                            <div className="reserve-card delay-approval-card" onClick={handleDelayApproval}>
                                <h3>延期审批</h3>
                            </div>
                        </>
                    )}

                    {/* 还车审批和用车报表对管理员和司机开放 */}
                    {(isAdmin || isDriver) && (
                        <>
                            {/* 还车审批 */}
                            <div className="reserve-card return-approval-card" onClick={handleReturnApproval}>
                                <h3>还车审批</h3>
                            </div>

                            {/* 用车报表 */}
                            <div className="reserve-card car-report-card" onClick={handleCarReport}>
                                <h3>用车报表</h3>
                            </div>
                        </>
                    )}


                </div>
            )}

            {/* 未绑定提示浮层 */}
            {unboundToast && (
                <div className="unbound-toast" onClick={() => navigate('/bind-account')}>
                    <span className="unbound-toast-text">{unboundToast}</span>
                    <span className="unbound-toast-action">去绑定</span>
                </div>
            )}
        </div>
    );
}
